import { format } from "date-fns";
import { Truck, Eye, Copy, User, Building2, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TruckFormData, ContactInfo } from "./PostTruckFormCard";

interface Props {
  form: TruckFormData;
  contactInfo: ContactInfo;
  hasSavedTruck: boolean;
  onDuplicate: () => void;
}

export default function PostTruckSummaryCard({ form, contactInfo, hasSavedTruck, onDuplicate }: Props) {
  const rows: { label: string; value: string }[] = [
    { label: "Origin", value: form.origin || "—" },
    { label: "Destination", value: form.openToAnywhere ? "Open to anywhere" : form.destination || "—" },
    { label: "Availability", value: form.availabilityDate ? format(form.availabilityDate, "MMM d, yyyy") : "—" },
    { label: "Equipment", value: form.equipmentType || "—" },
    { label: "Length", value: form.length ? `${form.length} ft` : "—" },
    { label: "Max weight", value: form.maxWeight ? `${Number(form.maxWeight).toLocaleString()} lbs` : "—" },
    { label: "Load type", value: form.loadType || "—" },
  ];

  const contactRows: { icon: React.ReactNode; label: string; value: string }[] = [
    { icon: <User className="h-3 w-3" />, label: "Contact", value: contactInfo.name || "—" },
    { icon: <Building2 className="h-3 w-3" />, label: "Company", value: contactInfo.companyName || "—" },
    { icon: <Phone className="h-3 w-3" />, label: "Phone", value: contactInfo.phone || "—" },
    { icon: <Mail className="h-3 w-3" />, label: "Email", value: contactInfo.email || "—" },
  ];

  return (
    <div
      className="rounded-xl border border-border bg-card sticky top-3 flex flex-col w-full min-w-0"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {/* Post Summary */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Eye className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground">Post Summary</h4>
      </div>

      <div className="px-4 py-3 space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-start justify-between gap-2">
            <span className="text-[10px] font-medium text-muted-foreground whitespace-nowrap">{r.label}</span>
            <span className="text-[11px] font-semibold text-foreground text-right truncate max-w-[180px]">{r.value}</span>
          </div>
        ))}
      </div>

      {/* Read-only Contact Info */}
      <div className="border-t border-border">
        <div className="flex items-center gap-2 px-4 py-3">
          <Phone className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground">Contact Info</h4>
        </div>
        <div className="px-4 pb-3 space-y-2">
          {contactRows.map((r) => (
            <div key={r.label} className="flex items-center gap-2">
              <span className="text-muted-foreground">{r.icon}</span>
              <span className="text-[10px] font-medium text-muted-foreground min-w-[52px]">{r.label}</span>
              <span className="text-[11px] font-semibold text-foreground truncate">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Duplicate Last Truck */}
      <div className="px-4 py-3 border-t border-border space-y-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-[11px] font-semibold gap-1.5"
          disabled={!hasSavedTruck}
          onClick={onDuplicate}
        >
          <Copy className="h-3.5 w-3.5" />
          Duplicate Last Truck
        </Button>
        {!hasSavedTruck && (
          <p className="text-[10px] text-muted-foreground text-center">Post a truck first to enable duplication.</p>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-3 border-t border-border">
        <button
          disabled
          className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground opacity-50 cursor-not-allowed"
        >
          <Truck className="h-3 w-3" />
          View your post after submission
        </button>
      </div>
    </div>
  );
}
